"use client";

import { useEffect, useState, useCallback, useRef } from "react";

export function RotatingWord({
  words,
  interval = 3000,
  initialDelay = 0,
  suffix = "",
}: {
  words: string[];
  interval?: number;
  initialDelay?: number;
  suffix?: string;
}) {
  const [active, setActive] = useState<"a" | "b">("a");
  const [slotA, setSlotA] = useState(0);
  const [slotB, setSlotB] = useState(1 % words.length);
  const [animating, setAnimating] = useState(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const completeTransition = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    setActive((prev) => (prev === "a" ? "b" : "a"));
    setAnimating(false);
  }, []);

  const startTransition = useCallback(() => {
    const nextIndex = ((active === "a" ? slotA : slotB) + 1) % words.length;
    if (active === "a") {
      setSlotB(nextIndex);
    } else {
      setSlotA(nextIndex);
    }
    requestAnimationFrame(() => {
      setAnimating(true);
      fallbackTimerRef.current = setTimeout(() => {
        fallbackTimerRef.current = null;
        completeTransition();
      }, 1500);
    });
  }, [active, slotA, slotB, words.length, completeTransition]);

  useEffect(() => {
    if (words.length <= 1) return;

    let intervalId: ReturnType<typeof setInterval>;
    const startTimeout = setTimeout(() => {
      intervalId = setInterval(startTransition, interval);
    }, initialDelay);

    return () => {
      clearTimeout(startTimeout);
      clearInterval(intervalId);
    };
  }, [words.length, interval, initialDelay, startTransition]);

  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, []);

  function handleTransitionEnd(e: React.TransitionEvent) {
    if (e.propertyName !== "transform") return;
    completeTransition();
  }

  const aIsActive = active === "a";
  const aVisible = aIsActive ? !animating : animating;
  const bVisible = aIsActive ? animating : !animating;

  return (
    <span
      className="block overflow-hidden relative"
      style={{ height: "1.18em" }}
    >
      {/* Slot A */}
      <span
        className={`absolute inset-x-0 top-0 block whitespace-nowrap transition-[transform,opacity] duration-[350ms] ease-out ${
          aVisible
            ? "opacity-100 translate-y-0"
            : aIsActive
            ? "opacity-0 -translate-y-full"
            : "opacity-0 translate-y-full"
        }`}
        onTransitionEnd={handleTransitionEnd}
      >
        {words[slotA]}{suffix}
      </span>

      {/* Slot B */}
      <span
        className={`absolute inset-x-0 top-0 block whitespace-nowrap transition-[transform,opacity] duration-[350ms] ease-out ${
          bVisible
            ? "opacity-100 translate-y-0"
            : !aIsActive
            ? "opacity-0 -translate-y-full"
            : "opacity-0 translate-y-full"
        }`}
        onTransitionEnd={handleTransitionEnd}
      >
        {words[slotB]}{suffix}
      </span>
    </span>
  );
}
